// R-audit-2026-09-26 D4.5 收尾:React Query queryKey 中心化常量
//
// 背景(见 memory topic-r262-round-5 §4.5):
// - R5-D4-008 (2026-08-24) 修了 ['points', projectId] vs ['projectPoints', ...] 死 invalidate
//   (中间夹 'project' 字符,React Query 5 prefix match 永远不命中)
// - 修法:统一 prefix + 删 10 处死 invalidate
// - R6+ 必做:中心化 queryKey,禁止字面量,防同类 anti-pattern 复发
//
// 用法:
//   import { qk } from '../constants/queryKeys'
//   queryKey: qk.projectPoints(projectId)
//   invalidateQueries({ queryKey: qk.projectPoints(projectId) })
//
// 新增 queryKey 步骤:
//   1. 在本文件 qk 对象加 factory 函数(参数化 key 用函数,固定 key 用字符串)
//   2. 替换所有 queryKey 字面量调用点
//   3. 严禁继续用 ['xxx', projectId] 字面量
//
// 注意事项:
// - 2-element factory 用于 prefix match invalidate(React Query 5 默认 prefix 匹配)
// - 3-element query key(如 projectPersons [projectId, personType])query 调用方必须显式传第二参数;
//   2-element prefix 工厂已能匹配 3-element 实际 key,无需为 query 调用单独提供 factory
// - admin / platform / schedule 等 module-level key(无 projectId)用固定字符串,见末尾

/**
 * React Query queryKey 工厂
 *
 * - 函数式 key 用于参数化(必须接受 projectId / userId / 等动态参数)
 * - 字符串式 key 用于无参固定值
 * - 所有 key 第一项必须用命名空间前缀(eg 'projectPoints', 'kanban-summary')避免不同业务同名冲突
 */
export const qk = {
  // ========== Points ==========
  projectPoints: (projectId) => ['projectPoints', String(projectId)],
  projectPointsForAreas: (projectId) => ['projectPointsForAreas', String(projectId)],

  // ========== Material ==========
  materialInventory: (projectId) => ['materialInventory', String(projectId)],
  materials: (projectId) => ['materials', String(projectId)],
  materialIns: (projectId) => ['materialIns', String(projectId)],
  materialOuts: (projectId) => ['materialOuts', String(projectId)],
  materialChecks: (projectId) => ['material-checks', String(projectId)],
  // ModuleMaterialsTable 用
  moduleMaterials: (projectId, group, page, pageSize, keyword) => ['moduleMaterials', String(projectId), group, page, pageSize, keyword],

  // ========== Document ==========
  documentView: (projectId) => ['documentView', String(projectId)],
  documentHandovers: (projectId) => ['document-handovers', String(projectId)],

  // ========== Contract ==========
  contractStats: (projectId) => ['contractStats', String(projectId)],
  contracts: (projectId) => ['contracts', String(projectId)],
  qsRecords: (projectId) => ['qsRecords', String(projectId)],
  qsStats: (projectId) => ['qsStats', String(projectId)],
  qsContracts: (projectId) => ['qsContracts', String(projectId)],
  invoices: (projectId) => ['invoices', String(projectId)],
  payments: (projectId) => ['payments', String(projectId)],

  // ========== Issue ==========
  // 多态:不传 type → 2-element(prefix invalidate);传 type → 3-element(queryKey)
  unifiedIssues: (projectId, type) => type != null
    ? ['unifiedIssues', String(projectId), type]
    : ['unifiedIssues', String(projectId)],
  safetyIssues: (projectId) => ['safetyIssues', String(projectId)],
  qualityIssues: (projectId) => ['qualityIssues', String(projectId)],
  safetyStats: (projectId) => ['safetyStats', String(projectId)],
  qualityStats: (projectId) => ['qualityStats', String(projectId)],
  safetyCosts: (projectId) => ['safetyCosts', String(projectId)],
  safetyLogs: (projectId) => ['safety-logs', String(projectId)],
  safetyCertificates: (projectId) => ['safetyCertificates', String(projectId)],
  safetyChecks: (projectId) => ['safetyChecks', String(projectId)],
  // 多态:不传 safetyLogId → 1-element(prefix invalidate);传 → 2-element(queryKey)
  safetyCheckIssues: (safetyLogId) => safetyLogId != null
    ? ['safety-check-issues', String(safetyLogId)]
    : ['safety-check-issues'],
  safetyMechanical: (projectId) => ['safetyMechanical', String(projectId)],
  qualityChecks: (projectId) => ['qualityChecks', String(projectId)],
  qualityAcceptances: (projectId) => ['qualityAcceptances', String(projectId)],
  // R-fix-2026-09-26 batch7+: QualityAcceptanceModal 用 acceptanceStages,但 D4.5 中心化批次漏加工厂
  acceptanceStages: (projectId) => ['acceptanceStages', String(projectId)],
  qualitySupervisions: (projectId) => ['qualitySupervisions', String(projectId)],
  qualitySupervisionMaterials: (projectId) => ['qualitySupervisionMaterials', String(projectId)],
  qualitySuppliers: (projectId) => ['qualitySuppliers', String(projectId)],
  inspections: (projectId) => ['inspections', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 projectId → 2-element;传 scope → 3-element(用 all/safety/quality)
  inspectionCount: (projectId, scope) => {
    if (projectId == null) return ['inspection-count']
    if (scope == null) return ['inspection-count', String(projectId)]
    return ['inspection-count', String(projectId), scope]
  },
  supervisionLogs: (projectId) => ['supervision-logs', String(projectId)],
  supervisionLogTemplates: () => ['supervision-log-templates'],

  // ========== Tasks ==========
  tasksManage: (projectId) => ['tasks-manage', String(projectId)],
  tasks: (projectId) => ['tasks', String(projectId)],
  // 多态:不传 type → 2-element(prefix invalidate);传 type → 3-element(queryKey)
  projectTasks: (projectId, type) => type != null
    ? ['projectTasks', String(projectId), type]
    : ['projectTasks', String(projectId)],

  // ========== Workplan ==========
  // 多态:不传 referenceDate → 2-element(prefix invalidate);传 → 3-element(queryKey)
  workplanQuadrant: (projectId, referenceDate) => referenceDate != null
    ? ['workplan-quadrant', String(projectId), referenceDate]
    : ['workplan-quadrant', String(projectId)],
  workplanConfig: (projectId) => ['workplan-config', String(projectId)],
  workplanPlans: (projectId) => ['workplan-plans', String(projectId)],

  // ========== Personnel ==========
  // 多态:不传 personType → 2-element(prefix invalidate);传 personType → 3-element(queryKey)
  projectPersons: (projectId, personType) => personType != null
    ? ['projectPersons', String(projectId), personType]
    : ['projectPersons', String(projectId)],
  projectCompanies: (projectId) => ['projectCompanies', String(projectId)],
  projectJobTypes: (projectId) => ['projectJobTypes', String(projectId)],
  // Admin.jsx 每个项目的成员列表(per-project cache key,useQueries 用)
  projectMembers: (projectId) => ['projectMembers', String(projectId)],

  // ========== MR ==========
  // Mr.jsx 多态:不传 keyword → 2-element(prefix invalidate);传 → 3-element(queryKey)
  mr: (projectId, keyword) => keyword != null
    ? ['mr', String(projectId), keyword]
    : ['mr', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  mrDetail: (id) => id != null
    ? ['mr-detail', String(id)]
    : ['mr-detail'],
  mrTransitions: (projectId) => ['mr-transitions', String(projectId)],

  // ========== Schedule ==========
  scheduleDataAudit: (projectId) => ['schedule', 'data-audit', String(projectId)],
  scheduleDependencies: (projectId) => ['schedule', 'dependencies', String(projectId)],
  scheduleGantt: (projectId) => ['schedule', 'gantt', String(projectId)],
  // 2026-10-16 P0-3:任务版本快照(参数化 task_id)
  taskVersions: (projectId, taskId) => ['schedule', 'task-versions', String(projectId), String(taskId)],

  // ========== Alerts ==========
  alertsCounts: (projectId) => ['alerts', 'counts', String(projectId)],
  // alertsItems 多态:tab 可能为 undefined(全部)
  alertsItems: (tab, projectId) => tab != null
    ? ['alerts', 'items', tab, String(projectId)]
    : ['alerts', 'items', String(projectId)],

  // ========== Procedures ==========
  procedures: (projectId) => ['procedures', String(projectId)],
  procedureStats: (projectId) => ['procedureStats', String(projectId)],

  // ========== Construction / Concealed / Equipment ==========
  constructionLogs: (projectId) => ['construction-logs', String(projectId)],
  concealed: (projectId) => ['concealed', String(projectId)],
  concealedDetail: (id) => id != null ? ['concealedDetail', String(id)] : ['concealedDetail'],
  // 多态:不传 filters → 2 元素(prefix invalidate);传 filters → 6 元素(列表 queryKey)
  progressPhotos: (projectId, ...filters) => filters.length > 0
    ? ['progressPhotos', String(projectId), ...filters.map(String)]
    : ['progressPhotos', String(projectId)],
  photoCategories: (projectId) => ['photoCategories', String(projectId)],
  photoStats: (projectId) => ['photoStats', String(projectId)],
  equipmentEntry: (projectId) => ['equipment-entry', String(projectId)],
  energizations: (projectId) => ['energizations', String(projectId)],

  // ========== Work Areas ==========
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  workAreas: (projectId) => projectId != null
    ? ['workAreas', String(projectId)]
    : ['workAreas'],
  workAreasList: (projectId) => ['workAreasList', String(projectId)],
  projectAreas: (projectId) => ['projectAreas', String(projectId)],
  // SharedData.jsx 用:workAreas 的 task 子列表
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  tasksForAreas: (projectId) => projectId != null
    ? ['tasksForAreas', String(projectId)]
    : ['tasksForAreas'],

  // ========== Work Permit / Handover / Site Contact ==========
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  workPermits: (projectId) => projectId != null
    ? ['work-permits', String(projectId)]
    : ['work-permits'],
  // 多态:不传 detailId → 2-element(prefix invalidate);传 detailId → 3-element(queryKey)
  workPermitDetail: (projectId, detailId) => detailId != null
    ? ['workPermitDetail', String(projectId), String(detailId)]
    : ['workPermitDetail', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  workHandovers: (projectId) => projectId != null
    ? ['work-handovers', String(projectId)]
    : ['work-handovers'],
  // 多态:不传 detailId → 2-element(prefix invalidate);传 → 3-element(queryKey)
  workHandoverDetail: (projectId, detailId) => detailId != null
    ? ['workHandoverDetail', String(projectId), String(detailId)]
    : ['workHandoverDetail', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  siteContacts: (projectId) => projectId != null
    ? ['site-contacts', String(projectId)]
    : ['site-contacts'],
  // 多态:不传 detailId → 2-element(prefix invalidate);传 → 3-element(queryKey)
  siteContactDetail: (projectId, detailId) => detailId != null
    ? ['siteContactDetail', String(projectId), String(detailId)]
    : ['siteContactDetail', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  sealUsages: (projectId) => projectId != null
    ? ['seal-usages', String(projectId)]
    : ['seal-usages'],

  // ========== SVO / MR ==========
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  svos: (projectId) => projectId != null
    ? ['svos', String(projectId)]
    : ['svos'],
  // 多态:不传 detailId → 2-element(prefix invalidate);传 → 3-element(queryKey)
  svoDetail: (projectId, detailId) => detailId != null
    ? ['svoDetail', String(projectId), String(detailId)]
    : ['svoDetail', String(projectId)],

  // ========== Risks ==========
  risks: (projectId) => ['risks', String(projectId)],
  riskStats: (projectId) => ['riskStats', String(projectId)],

  // ========== Meetings / Reports / Regulations ==========
  meetings: (projectId) => ['meetings', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  reportTemplates: (projectId) => projectId != null
    ? ['report-templates', String(projectId)]
    : ['report-templates'],
  regulations: (projectId) => projectId != null
    ? ['regulations', String(projectId)]
    : ['regulations'],

  // ========== Training ==========
  trainingSessions: (projectId) => ['trainingSessions', String(projectId)],

  // ========== Webhooks / External Notify ==========
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  webhooks: (projectId) => projectId != null
    ? ['webhooks', String(projectId)]
    : ['webhooks'],
  webhookEvents: (projectId) => projectId != null
    ? ['webhook-events', String(projectId)]
    : ['webhook-events'],
  webhookDeliveries: (historyDrawer) => historyDrawer != null
    ? ['webhook-deliveries', historyDrawer]
    : ['webhook-deliveries'],
  externalNotifySettings: (orgId) => ['external-notify-settings', String(orgId)],
  onpremConfig: (orgId) => ['onprem-config', String(orgId)],

  // ========== Design ==========
  designChanges: (projectId) => ['designChanges', String(projectId)],
  designDeliverables: (projectId) => ['designDeliverables', String(projectId)],
  designReviews: (projectId) => ['designReviews', String(projectId)],
  designView: (projectId) => ['designView', String(projectId)],

  // ========== Investment / Portfolio ==========
  investmentRatioBatch: (projectId) => ['investmentRatioBatch', String(projectId)],
  paidRatioBatch: (projectId) => ['paidRatioBatch', String(projectId)],
  // R-audit-2026-10-05 di-34: 字面量 queryKey ['investment-dashboard', projectId] 改 qk factory
  investmentDashboard: (projectId) => ['investment-dashboard', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  portfolioSummary: (projectId) => projectId != null
    ? ['portfolio-summary', String(projectId)]
    : ['portfolio-summary'],
  portfolioProjects: (projectId) => projectId != null
    ? ['portfolio-projects', String(projectId)]
    : ['portfolio-projects'],
  portfolioGantt: (projectId) => projectId != null
    ? ['portfolio-gantt', String(projectId)]
    : ['portfolio-gantt'],
  portfolioCompare: (projectId) => projectId != null
    ? ['portfolio-compare', String(projectId)]
    : ['portfolio-compare'],

  // ========== Dashboard / KPI / EVM / S-curve ==========
  dashboardKpis: (projectId) => ['dashboard-kpis', String(projectId)],
  dashboardWidgetsMe: () => ['dashboard-widgets-me'],
  evm: (projectId) => ['evm', String(projectId)],
  sCurve: (projectId) => ['s-curve', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  healthBatch: (projectId) => projectId != null
    ? ['health-batch', String(projectId)]
    : ['health-batch'],
  healthRanking: (projectId) => ['health-ranking', String(projectId)],

  // ========== Schedule ==========
  schedule: (projectId) => ['schedule', String(projectId)],
  scheduleBaselines: (projectId) => ['schedule-baselines', String(projectId)],
  scheduleBaselineComparison: (projectId, baselineId) => baselineId != null
    ? ['schedule-baseline-comparison', String(projectId), String(baselineId)]
    : ['schedule-baseline-comparison', String(projectId)],

  // ========== Projects / Areas ==========
  // 多态:0 arg → ['projects'](prefix invalidate for cross-page use);
  //        >=1 arg → ['projects', ...seg] (queryKey for per-user/filter scoping)
  projects: (...segments) => segments.length > 0
    ? ['projects', ...segments.map(s => s == null ? '' : String(s))]
    : ['projects'],
  // ========== Project Quantities (Dashboard 工程量列表) ==========
  projectQuantities: (projectId) => ['project-quantities', String(projectId ?? '')],
  // ========== Current Project (全局 hook useCurrentProject) ==========
  currentProject: (projectId) => ['project', String(projectId ?? '')],

  // ========== Kanban ==========
  kanbanSummary: (projectId) => ['kanban-summary', String(projectId)],

  // ========== Map ==========
  mapState: (projectId) => ['mapState', String(projectId)],

  // ========== Comments / Notifications / Alerts ==========
  commentCounts: (projectId) => ['comment-counts', String(projectId)],
  comments: (projectId) => ['comments', String(projectId)],
  notifications: (userId) => ['notifications', String(userId ?? '')],
  notificationUnreadCount: (userId) => ['notification-unread-count', String(userId ?? '')],
  alerts: () => ['alerts'],
  actionItems: (projectId) => ['action-items', String(projectId)],
  myApprovals: (userId) => ['my-approvals', String(userId ?? '')],

  // ========== Resource Pool ==========
  resourcePoolPersonnel: () => ['resource-pool-personnel'],
  resourcePoolEquipment: () => ['resource-pool-equipment'],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  workloadPersonnel: (projectId) => projectId != null
    ? ['workload-personnel', String(projectId)]
    : ['workload-personnel'],
  workloadEquipment: (projectId) => projectId != null
    ? ['workload-equipment', String(projectId)]
    : ['workload-equipment'],

  // ========== Contract Party B / Site Mgmt Count ==========
  contractPartyB: (projectId) => ['contractPartyB', String(projectId)],
  contractsForSiteContact: (projectId) => ['contracts-for-site-contact', String(projectId)],
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  siteMgmtCount: (projectId) => projectId != null
    ? ['site-mgmt-count', String(projectId)]
    : ['site-mgmt-count'],
  projectsForTimesheet: () => ['projects-for-timesheet'],

  // ========== Timesheet / Calendar ==========
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  timesheetMe: (userId) => userId != null
    ? ['timesheet-me', String(userId)]
    : ['timesheet-me'],
  // 多态:日历 query 多参,本 factory 仅 2-element 用于 prefix invalidate
  calendarEvents: (...args) => args.length > 0
    ? ['calendar-events', ...args.map(String)]
    : ['calendar-events'],
  calendarPageProjects: () => ['calendar-page-projects'],
  calendarProjectOptionsFallback: () => ['calendar-project-options-fallback'],

  // ========== Mention / Shared Users ==========
  sharedUsers: () => ['shared-users'],
  sharedUsersForMention: () => ['shared-users-for-mention'],
  personsForDoc: (projectId) => ['persons-for-doc', String(projectId)],

  // ========== Plan Levels / Permissions ==========
  planLevelsAccessible: (userId) => ['plan-levels-accessible', String(userId ?? '')],
  permissions: (userId) => ['permissions', String(userId ?? '')],

  // ========== Admin / Platform / Audit ==========
  // 多态:admin 类 prefix 不同后缀(常见 ['admin'] / ['admin', 'projects'] / ['admin', 'org-projects', orgId])
  admin: (...segments) => segments.length > 0
    ? ['admin', ...segments.map(String)]
    : ['admin'],
  adminProjects: () => ['admin-projects'],
  // 变参:不传 → 1-element(prefix invalidate);传 filters → N-element(queryKey)
  adminArchives: (...filters) => filters.length > 0
    ? ['admin-archives', ...filters.map(f => f == null ? '' : String(f))]
    : ['admin-archives'],
  adminBackups: () => ['admin-backups'],
  // R-fix-2026-09-26 batch7+: PlatformAdmin + TestData + BackupPanel 三处共用,但 D4.5 中心化漏加工厂
  platformOrganizations: () => ['platform', 'organizations'],
  platformOrgMembers: (orgId) => ['platform', 'org-members', String(orgId ?? '')],
  platform: (...segments) => segments.length > 0
    ? ['platform', ...segments.map(String)]
    : ['platform'],
  orgProjects: (orgId) => ['admin', 'org-projects', String(orgId)],
  orgMembersForProject: (orgId, userId) => ['admin', 'org-members-for-project', String(orgId), String(userId ?? 'anon')],

  // ========== API Keys / Auth / Login Logs ==========
  apiKeys: (orgId) => ['api-keys', String(orgId ?? '')],
  auth: () => ['auth'],
  // 变参:不传 → 1-element(prefix invalidate);传 filters → N-element(queryKey)
  loginLogs: (...filters) => filters.length > 0
    ? ['login-logs', ...filters.map(f => f == null ? '' : String(f))]
    : ['login-logs'],

  // ========== OpenAPI / Operation Logs / AI ==========
  openapiSpec: () => ['openapi-spec'],
  // 变参:不传 → 1-element(prefix invalidate);传 filters → N-element(queryKey)
  operationLogs: (...filters) => filters.length > 0
    ? ['operation-logs', ...filters.map(f => f == null ? '' : String(f))]
    : ['operation-logs'],
  opLogOptions: () => ['op-log-options'],
  aiProviderInfo: () => ['ai-provider-info'],

  // ========== Calc Settings ==========
  calcSettingsMe: () => ['calc-settings-me'],

  // ========== 2FA / Security ==========
  twoFactorStatus: () => ['2fa-status'],

  // ========== Approval Instance ==========
  approvalInstance: (id) => ['approval-instance', String(id ?? '')],

  // ========== Global Search ==========
  // 多态:不传 → 1-element(prefix invalidate);传 → 2-element(queryKey)
  globalSearch: (query) => query != null
    ? ['global-search', String(query)]
    : ['global-search'],

  // ========== Health / Calc / Personal Plans / Work Logs ==========
  // 九维权重(项目级);D4.5/HealthCards/CalcParamsModal 用
  healthWeights: (projectId) => ['health', String(projectId ?? ''), 'weights'],
  // 组织级计算参数(A/B/C 切点 + EVM 区间)
  calcSettings: () => ['calc-settings'],
  // 个人计划列表;D4.5 收尾(原 PersonalPlansWidget 直接用 ['personal-plans', ...])
  personalPlans: (userId) => userId != null
    ? ['personal-plans', String(userId)]
    : ['personal-plans'],
  // 工作日志评论;LogComments.jsx 用
  workLogComments: (logId) => ['work-log-comments', String(logId)],
  // 工作日志模板;WorkLogTemplates.jsx 用
  workLogTemplates: () => ['work-log-templates'],

  // ========== Workbench (跨分区聚合页,MyWorkbench.jsx) ==========
  // 多态:summary/projects/my-tasks/my-procedures/.../my-mentions 等后缀拼成 queryKey;
  // 0 arg → ['workbench'](整页失效不常用);实际调用方都带后缀
  workbench: (...segments) => segments.length > 0
    ? ['workbench', ...segments.map(s => s == null ? '' : String(s))]
    : ['workbench'],

  // ========== Report Share ==========
  // 报告分享 token(R-feat-2026-09-05 访客只读页用)
  shareReport: (token) => ['share-report', String(token ?? '')],

  // ========== Document Check Criteria ==========
  // 资料检查规则列表(DocumentCheckCriteria.jsx 用)
  docCheckCriteriaList: () => ['doc-check-criteria-list'],

  // ========== User Favorites (R-feat-2026-10-14 #5 易用性) ==========
  // 项目收藏(GET /api/user/favorite-projects)
  // 2-element 用于 prefix invalidate(invalidate ['user-favorites'] 时匹配)
  userFavorites: (userId) => userId != null
    ? ['user-favorites', String(userId)]
    : ['user-favorites'],

  // ========== Action Center (R-feat-2026-10-14 #2 易用性 — 工作台 Action Center) ==========
  // 跨模块待办聚合(approval / overdue / today),GET /api/action-center?category=...
  // 2-element 形式:invalidate ['action-center'] 可匹配所有 category 维度
  actionCenter: (uid, category) => category
    ? ['action-center', String(uid ?? ''), String(category)]
    : ['action-center', String(uid ?? '')],
}